import { ServerResponse, type IncomingMessage } from "http"
import type { NextApiRequest, NextApiResponse } from "next"
import { NextResponse } from "next/server"
import { edgeAddSecondsToDate, edgeCreateDate, needsSSL } from "@/proxy-utils"
import {
  AUTH_TOKEN_COOKIE,
  AuthToken,
  AuthV2Response,
  MINDTRIP_LOCALE_COOKIE,
  NEXTAUTH_CALLBACK_URL_DEV,
  NEXTAUTH_CALLBACK_URL_PROD,
  NEXTAUTH_CSRF_TOKEN_DEV,
  NEXTAUTH_CSRF_TOKEN_PROD,
  NEXTAUTH_SESSION_TOKEN_DEV,
  NEXTAUTH_SESSION_TOKEN_PROD,
  OLD_AUTH_COOKIE,
  PKCE_CODE_VERIFIER_DEV,
  PKCE_CODE_VERIFIER_PROD,
  REFRESH_TOKEN_COOKIE,
  RefreshToken,
  SESSION_PROPS_COOKIE,
} from "@/types/auth"
import { deleteCookie, getCookie, setCookie } from "cookies-next"

import { env } from "@/lib/env"

// Recommended max cookie age for refresh tokens to avoid browser limits
export const MAX_COOKIE_AGE_SECONDS = 400 * 24 * 60 * 60 // 400 days in seconds

export const AUTH_COOKIES = [
  // Backend Rails auth cookies
  AUTH_TOKEN_COOKIE,
  REFRESH_TOKEN_COOKIE,

  // [AuthCleanup] Legacy cookies - remove these when legacy support is no longer needed
  OLD_AUTH_COOKIE, // Legacy anonymous user cookie format
  NEXTAUTH_SESSION_TOKEN_PROD,
  NEXTAUTH_SESSION_TOKEN_DEV,
  PKCE_CODE_VERIFIER_PROD,
  PKCE_CODE_VERIFIER_DEV,
  NEXTAUTH_CSRF_TOKEN_PROD,
  NEXTAUTH_CSRF_TOKEN_DEV,
  NEXTAUTH_CALLBACK_URL_PROD,
  NEXTAUTH_CALLBACK_URL_DEV,
]

const OTHER_USER_COOKIES = [SESSION_PROPS_COOKIE]

const IGNORED_COOKIES: string[] = [
  // Cookies we don't want to clear (case-insensitive patterns).
  // mindtrip.locale persists per-browser like mindtrip.uiSettings (both host-only, so a
  // domain-scoped delete here wouldn't match anyway); LocaleHandler rewrites it whenever
  // the resolved locale changes.
  MINDTRIP_LOCALE_COOKIE,
]

export function getCookieDomain(): string {
  const cookieAuthDomain = env("NEXT_COOKIE_AUTH_DOMAIN")
  if (!cookieAuthDomain) {
    throw new Error("NEXT_COOKIE_AUTH_DOMAIN is not set.")
  }
  return cookieAuthDomain
}

// Common cookie configuration
interface CookieConfig {
  domain: string
  path: string
  secure: boolean
  sameSite: "lax"
  httpOnly: boolean
}

function getBaseCookieConfig(httpOnly = true): CookieConfig {
  return {
    domain: getCookieDomain(),
    path: "/",
    secure: needsSSL(),
    sameSite: "lax",
    httpOnly,
  }
}

function ignoredCookie(cookieName: string): boolean {
  const cookieLower = cookieName.toLowerCase()

  // Check exact matches in IGNORED_COOKIES array (case-insensitive)
  if (
    IGNORED_COOKIES.some((ignored) => ignored.toLowerCase() === cookieLower)
  ) {
    return true
  }

  // Check for AMP cookies (case-insensitive)
  if (/^amp_/i.test(cookieLower)) {
    return true
  }

  // Check for GA cookies (case-insensitive)
  if (/^_ga/i.test(cookieLower)) {
    return true
  }

  if (/^g_/i.test(cookieLower)) {
    return true
  }

  // Check for Heroku session affinity cookie (case-insensitive)
  if (cookieLower === "heroku-session-affinity") {
    return true
  }

  return false
}

function knownCookie(cookieName: string, cookiesToClear: string[]): boolean {
  const cookieLower = cookieName.toLowerCase()
  return cookiesToClear.some((known) => known.toLowerCase() === cookieLower)
}

// Edge-runtime safe functions for middleware
export function setAuthTokenCookieEdge(
  response: NextResponse,
  authToken: AuthToken
): void {
  const config = getBaseCookieConfig(true)
  const expires = edgeAddSecondsToDate(
    edgeCreateDate(),
    authToken.expires_in - 10 // 10 second buffer
  )

  response.cookies.set({
    name: AUTH_TOKEN_COOKIE,
    value: authToken.value,
    ...config,
    expires,
  })
}

export function setRefreshTokenCookieEdge(
  response: NextResponse,
  refreshToken: RefreshToken
): void {
  const config = getBaseCookieConfig(true)
  const expiresIn = refreshToken.expires_in || MAX_COOKIE_AGE_SECONDS
  const expires = edgeAddSecondsToDate(
    edgeCreateDate(),
    expiresIn - 10 // 10 second buffer
  )

  response.cookies.set({
    name: REFRESH_TOKEN_COOKIE,
    value: refreshToken.value,
    ...config,
    expires,
  })
}

export function deleteAuthCookiesEdge(response: NextResponse): void {
  const config = getBaseCookieConfig(true)

  AUTH_COOKIES.forEach((cookieName) => {
    response.cookies.set({
      name: cookieName,
      value: "",
      ...config,
      maxAge: -1,
    })
  })
}

// API route functions using cookies-next
export function setAuthTokenCookieApi(
  req: NextApiRequest,
  res: NextApiResponse,
  authToken: AuthToken
): void {
  const config = getBaseCookieConfig(true)
  const expires = edgeAddSecondsToDate(
    edgeCreateDate(),
    authToken.expires_in - 10 // 10 second buffer
  )

  setCookie(AUTH_TOKEN_COOKIE, authToken.value, {
    req,
    res,
    ...config,
    expires,
  })
}

// SSR-compatible version for getServerSideProps context
export function setAuthTokenCookieSSR(
  req: NextApiRequest | IncomingMessage,
  res: NextApiResponse | ServerResponse,
  authToken: AuthToken
): void {
  const config = getBaseCookieConfig(true)
  const expires = edgeAddSecondsToDate(
    edgeCreateDate(),
    authToken.expires_in - 10 // 10 second buffer
  )

  setCookie(AUTH_TOKEN_COOKIE, authToken.value, {
    req,
    res,
    ...config,
    expires,
  })
}

export function setRefreshTokenCookieApi(
  req: NextApiRequest,
  res: NextApiResponse,
  refreshToken: RefreshToken
): void {
  const config = getBaseCookieConfig(true)
  const expiresIn = refreshToken.expires_in || MAX_COOKIE_AGE_SECONDS
  const expires = edgeAddSecondsToDate(
    edgeCreateDate(),
    expiresIn - 10 // 10 second buffer
  )

  setCookie(REFRESH_TOKEN_COOKIE, refreshToken.value, {
    req,
    res,
    ...config,
    expires,
  })
}

export function deleteAuthCookieApi(
  req: NextApiRequest,
  res: NextApiResponse,
  cookieName: string,
  isHttpOnly = true
): void {
  const config = getBaseCookieConfig(isHttpOnly)

  deleteCookie(cookieName, {
    req,
    res,
    ...config,
    maxAge: -1,
  })
}

// [AuthCleanup] This function deletes ALL cookies in AUTH_COOKIES array
// Including NextAuth cookies which cause __Secure- prefix errors on localhost
// When removing legacy support, only delete AUTH_TOKEN_COOKIE and REFRESH_TOKEN_COOKIE
export function deleteAllAuthCookiesApi(
  req: NextApiRequest,
  res: NextApiResponse
): void {
  AUTH_COOKIES.forEach((cookieName) => {
    // Auth cookies are httpOnly
    deleteAuthCookieApi(req, res, cookieName, true)
  })
}

export function clearAllUserCookies(req: NextApiRequest, res: NextApiResponse) {
  const cookiesToClear = [...AUTH_COOKIES, ...OTHER_USER_COOKIES]

  const unknownCookies: string[] = []

  Object.keys(req.cookies).forEach((cookieName) => {
    if (knownCookie(cookieName, cookiesToClear)) {
      // Auth cookies (auth_token, refresh_token) are httpOnly
      // Other cookies (session_props) are not httpOnly
      const isHttpOnly = AUTH_COOKIES.includes(cookieName)
      deleteAuthCookieApi(req, res, cookieName, isHttpOnly)
    } else if (ignoredCookie(cookieName)) {
      return
    } else {
      unknownCookies.push(cookieName)
    }
  })

  // Log any unknown cookies for investigation
  if (unknownCookies.length > 0) {
    console.info(`[auth_v2] [unknown_cookies] ${unknownCookies.join(", ")}`)
  }
}

// Apply auth cookies for API routes
export function applyAuthCookie(
  req: NextApiRequest,
  res: NextApiResponse,
  authRes: AuthV2Response
): void {
  const authToken = authRes.auth_token
  if (!authToken) {
    throw new Error("AuthV2Response missing auth_token")
  }

  // Clear existing auth cookies first
  deleteAllAuthCookiesApi(req, res)

  setAuthTokenCookieApi(req, res, authToken)
  if (authRes.refresh_token) {
    setRefreshTokenCookieApi(req, res, authRes.refresh_token)
  }
}

// Apply auth cookies for middleware
export function applyAuthCookieMiddleware(
  response: NextResponse,
  authRes: AuthV2Response
): void {
  const authToken = authRes.auth_token
  if (!authToken) {
    throw new Error("AuthV2Response missing auth_token")
  }

  setAuthTokenCookieEdge(response, authToken)
  if (authRes.refresh_token) {
    setRefreshTokenCookieEdge(response, authRes.refresh_token)
  }
}

// Backward compatibility wrapper
export function applyAuthCookies(
  authRes: AuthV2Response,
  req: NextApiRequest,
  res: NextApiResponse
) {
  applyAuthCookie(req, res, authRes)
}

export function getRefreshTokenFromCookie(
  req: NextApiRequest,
  res: NextApiResponse
): string | undefined {
  const refreshToken = getCookie(REFRESH_TOKEN_COOKIE, { req, res })
  return typeof refreshToken === "string" ? refreshToken : undefined
}

export function getAuthCookiesPresent(
  cookies: Partial<{ [key: string]: string }>
): string[] {
  const authCookies = [
    AUTH_TOKEN_COOKIE,
    REFRESH_TOKEN_COOKIE,
    OLD_AUTH_COOKIE,
    NEXTAUTH_SESSION_TOKEN_PROD,
    NEXTAUTH_SESSION_TOKEN_DEV,
  ]

  return authCookies.filter((cookieName) => cookies[cookieName] !== undefined)
}
